import { createListOfErrorsPerSkuFromUpdateResultGroup } from '../utils/createListOfErrorsPerSkuFromUpdateResultGroup';
import { extractWarningMessagesFromUpdateResultGroup } from '../utils/extractWarningMessagesFromUpdateResultGroup';
import { filterProductWarnings } from '../utils/filterProductWarnings';
import countItemsInBasket from './countItemsInBasket';

const resetProductWarningsForSkus = (productWarnings, items) =>
  filterProductWarnings(productWarnings, sku => !(sku in items));

const updateItems = ({ basketItems, items, key, value }) => Object.keys(items)
  .reduce((markedBasketItems, skuToMark) => {
    let nextBasketItems = [...markedBasketItems];

    // If we don't have this SKU in the basket yet, create an item for it
    if (!nextBasketItems.find(basketItem => basketItem.sku === skuToMark)) {
      nextBasketItems.unshift({
        sku: skuToMark,
        quantity: 0,
      });
    }

    // If we have this SKU in the basket already
    nextBasketItems = nextBasketItems.map(basketItem => (
      basketItem.sku === skuToMark
        ? {
          ...basketItem,
          [key]: value instanceof Function ? value(basketItem) : value,
        }
        : basketItem
    ));

    return nextBasketItems;
  }, basketItems);

const IS_UPDATING = 'isUpdating';
const QUANTITY = 'quantity';

const setItemsUpdatingState = (basketItems, items, isUpdating) =>
  updateItems({
    basketItems,
    items,
    key: IS_UPDATING,
    value: isUpdating,
  });

const markItemsAsUpdating = (basketItems, items) => setItemsUpdatingState(basketItems, items, true);
const markItemsAsNotUpdating = (basketItems, items) => setItemsUpdatingState(basketItems, items, false);

const changeItemsQuantity = (basketItems, items) => updateItems({
  basketItems,
  items,
  key: QUANTITY,
  value: basketItem => basketItem.quantity + items[basketItem.sku],
});

const rollbackItemsQuantity = (basketItems, items) => updateItems({
  basketItems,
  items,
  key: QUANTITY,
  value: basketItem => basketItem.quantity - items[basketItem.sku],
})
  .filter(item => item.quantity);

export function updateBasketRequest(state, action) {
  const { items: actionItems } = action.requestParams.body;

  let nextItems;
  if (action.actionParams && action.actionParams.enrichedItems) {
    nextItems = action.actionParams.enrichedItems;
  } else {
    nextItems = state.items;
    nextItems = changeItemsQuantity(nextItems, actionItems);
  }
  nextItems = markItemsAsUpdating(nextItems, actionItems);

  const nextNumberOfItems = countItemsInBasket(nextItems);

  const nextProductWarnings = resetProductWarningsForSkus(state.productWarnings, actionItems);

  const nextPromotionsExceedingLimit = [];

  return {
    ...state,
    items: nextItems,
    numberOfItems: nextNumberOfItems,
    productWarnings: nextProductWarnings,
    promotionsExceedingLimit: nextPromotionsExceedingLimit,
  };
}

export function updateBasketSuccess(state, action) {
  const { items: actionItems } = action.requestParams.body;
  const { updateResults, promotionsExceedingLimit: nextPromotionsExceedingLimit = [] } = action.data;

  let nextItems = state.items;
  nextItems = markItemsAsNotUpdating(nextItems, actionItems);
  // No need to change items quantity - optimistic update already did it

  const nextProductWarnings = createListOfErrorsPerSkuFromUpdateResultGroup(updateResults);
  const nextProductWarningsMessages = extractWarningMessagesFromUpdateResultGroup(updateResults);

  return {
    ...state,
    items: nextItems,
    productWarnings: nextProductWarnings,
    productWarningsMessages: nextProductWarningsMessages,
    promotionsExceedingLimit: nextPromotionsExceedingLimit,
  };
}

export function updateBasketError(state, action) {
  const { items: actionItems } = action.requestParams.body;

  let nextItems = state.items;
  nextItems = markItemsAsNotUpdating(nextItems, actionItems);
  nextItems = rollbackItemsQuantity(nextItems, actionItems);

  const nextNumberOfItems = countItemsInBasket(nextItems);

  return {
    ...state,
    error: action.error,
    items: nextItems,
    numberOfItems: nextNumberOfItems,
  };
}
